import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Industry data with CPR and CPM values
export const INDUSTRY_DATA = [
  { id: 'alimentos', name: 'Alimentos y bebidas / Materias primas', cpr: 80, cpm: 40 },
  { id: 'hogar', name: 'Hogar, decoración y mascotas', cpr: 66, cpm: 33 },
  { id: 'automotriz', name: 'Automotriz', cpr: 85, cpm: 42.50 },
  { id: 'herramientas', name: 'Herramientas e insumos', cpr: 85, cpm: 42.50 },
  { id: 'tecnologia', name: 'Tecnología', cpr: 90, cpm: 45 },
  { id: 'servicios', name: 'Servicios contables y legales', cpr: 78, cpm: 39 },
  { id: 'moda', name: 'Moda', cpr: 89, cpm: 44.50 },
  { id: 'industrial', name: 'Industrial / Construcción', cpr: 90, cpm: 45 },
  { id: 'inmobiliaria', name: 'Inmobiliaria', cpr: 140, cpm: 70 },
  { id: 'medico', name: 'Sector médico', cpr: 60, cpm: 30 },
  { id: 'otro', name: 'Otra industria', cpr: 80, cpm: 40 },
];

// Default values
export const DEFAULTS = {
  adjustmentFactor: 15,
  daysPerMonth: 30,
  frequency: 1.5,
  minReachSuggested: 12000,
  minInvestmentWarning: 1500,
  metaTiktokSplit: { meta: 70, tiktok: 30 },
  defaultIndustry: 'alimentos',
  defaultEffectiveness: 20,
  minBudgetLeads: 3000,
  minBudgetTraffic: 1500,
};

// Tooltips text
export const TOOLTIPS = {
  cpc: 'Costo que se paga por cada clic en tu anuncio. Varía por industria, competencia y calidad del anuncio. Utiliza el Planeador de Palabras Clave de Google Ads para tener mayor precisión.',
  cpr: 'Costo por Resultado. Lo que pagas por cada prospecto o venta generada.',
  cpm: 'Costo por cada 1,000 veces que se mostrará tu anuncio (impresiones). No garantiza clics ni conversiones, solo visibilidad.',
  effectiveness: 'De cada 100 prospectos que contactan al cliente, ¿cuántos compran? Usa el dato histórico del cliente o estima 20% si no lo sabe.',
  adjustmentFactor: 'Los primeros 1-3 meses las campañas no están optimizadas. Este margen nos da un rango mínimo de inversión pero tomará más tiempo llegar a los resultados esperados.',
  frequency: 'Número de veces que se mostrará el anuncio a cada persona. Un valor de 1.5 significa que en promedio cada persona verá el anuncio 1.5 veces.',
  ticketPromedio: 'Precio promedio del producto o servicio que vende el cliente.',
  metaVentas: 'Objetivo de ingresos que el cliente quiere alcanzar en el período.',
  clicsDiarios: 'Se sugieren 25 clics diarios como mínimo para tener conversiones diarias.',
  alcance: 'Número de personas únicas a las que se quiere impactar con la campaña.',
};

// Initial state
const initialState = {
  currentModule: null, // 'google', 'meta_leads', 'meta_traffic'
  history: [], // Last 3 calculations
  currency: 'MXN',
};

// Actions
const ACTIONS = {
  SET_MODULE: 'SET_MODULE',
  ADD_TO_HISTORY: 'ADD_TO_HISTORY',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
  SET_CURRENCY: 'SET_CURRENCY',
};

// Reducer
function calculatorReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_MODULE:
      return { ...state, currentModule: action.payload };
    case ACTIONS.ADD_TO_HISTORY:
      const newHistory = [action.payload, ...state.history].slice(0, 3);
      return { ...state, history: newHistory };
    case ACTIONS.CLEAR_HISTORY:
      return { ...state, history: [] };
    case ACTIONS.SET_CURRENCY:
      return { ...state, currency: action.payload };
    default:
      return state;
  }
}

// Context
const CalculatorContext = createContext(null);

// Provider
export function CalculatorProvider({ children }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const setModule = useCallback((module) => {
    dispatch({ type: ACTIONS.SET_MODULE, payload: module });
  }, []);

  const addToHistory = useCallback((calculation) => {
    dispatch({ 
      type: ACTIONS.ADD_TO_HISTORY, 
      payload: { ...calculation, timestamp: new Date().toISOString() } 
    });
  }, []);

  const clearHistory = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_HISTORY });
  }, []);

  const setCurrency = useCallback((currency) => {
    dispatch({ type: ACTIONS.SET_CURRENCY, payload: currency });
  }, []);

  const value = {
    ...state,
    setModule,
    addToHistory,
    clearHistory,
    setCurrency,
  };

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
}

// Hook
export function useCalculator() {
  const context = useContext(CalculatorContext);
  if (!context) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
}

// Format currency helper
export function formatCurrency(value, currency = 'MXN') {
  if (value === null || value === undefined || isNaN(value)) return '$0';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Format number helper
export function formatNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return new Intl.NumberFormat('es-MX').format(Math.round(value));
}
